import { getCustomersByClinicIdApi, fetchClinicDetailsApi } from '@/api/setting';
import { fetchPatientsByIdsApi } from '@/api/patient';
import {
  getMetricsApi,
  retrieveConversationsApi,
  getOrderRevenueMovementApi,
  getPreferencesApi,
  updatePreferencesApi,
  getRiskAlertsApi,
  resolveRiskAlertApi,
  getAIInsightsApi,
  submitAIFeedbackApi,
  getTransactionsApi,
  exportTransactionsApi,
  downloadTransactionExportApi,
  getDocumentsApi,
  batchDeleteDocumentsApi,
} from '@/api/clinicDashboard';

// LocalStorage keys for persisting dashboard and table filters
const DASHBOARD_FILTERS_STORAGE_KEY = 'clinicDashboard_filters';
const DOCUMENTS_TABLE_FILTERS_STORAGE_KEY = 'clinicDashboard_documentsFilters';
const TRANSACTIONS_TABLE_FILTERS_STORAGE_KEY = 'clinicDashboard_transactionsFilters';

// Helper function to load filters from localStorage
const loadFiltersFromStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const output =  { ...defaultValue, ...JSON.parse(saved) };
      if(output.page){
        //don't remember the page number
        output.page = 1;
      }
      return output;
    }
  } catch (error) {
    console.error(`Failed to load ${key} from localStorage:`, error);
  }
  return defaultValue;
};

const getDefaultState = () => ({
  // Whole page loading state
  isPageLoading: false,

  // Current Clinic Data
  clinicInfoAdminLevel: {
    practice_name: '',
    contact_info: { office_phone: '' },
    office_address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    },
  },
  clinicInfoProviderLevel: {
    practice_name: '',
    contact_info: { office_phone: '' },
    office_address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    },
  },

  // Dashboard Filters & Time Range
  dashboardFilters: {
    timeRange: 'last_30_days',
    startDate: null,
    endDate: null,
    dashboardScope: 'all',
  },

  // Metrics data (keyed by metric_id)
  metrics: {},
  cacheUpdateTime: null,

  // KPIs preferences
  kpis: {
    pinned_metric_ids: [],
    key_metric_ids: [],
    provider_id: null,
    updated_at: null,
  },

  // Patient Engagement Quality Summary
  patientEngagementQualitySummary: null,
  patientEngagementQualityLoading: false,

  // Active Alerts Data
  activeAlertsData: [],
  activeAlertsLoading: false,

  // Order and revenue chart data
  orderRevenueChartData: {
    buckets: [],
    total_created_orders_amount: 0,
    total_canceled_orders_amount: 0,
    total_net_order_amount: 0,
    cached_at: null,
    cache_status: null,
  },
  orderRevenueChartLoading: false,

  // Practice documents data
  practiceDocumentsData: {
    items: [],
    count: 0,
    total: 0,
  },
  practiceDocumentsTableFilters: loadFiltersFromStorage(DOCUMENTS_TABLE_FILTERS_STORAGE_KEY, {
    search: '',
    documentType: '',
    page: 1,
    pageSize: 10,
  }),
  checkedPracticeDocumentIds: [],
  practiceDocumentsTableLoading: false,

  // Transactions data
  transactionsData: {
    transactions: [],
    next_cursor: null,
    total: 0,
    current_page: 1,
    total_pages: 1,
    has_next_page: false,
  },
  transactionsTableFilters: loadFiltersFromStorage(TRANSACTIONS_TABLE_FILTERS_STORAGE_KEY, {
    search: '',
    payerType: '',
    transactionType: '',
    paymentMethod: '',
    timeRange: 'last_30_days',
    startDate: null,
    endDate: null,
    page: 1,
    pageSize: 10,
  }),
  checkedTransactionIds: [],
  transactionsTableLoading: false,

  // AI Insights (only fetched on user click)
  aiInsights: null,
  aiInsightsLoading: false,

  // AI Panel visibility
  aiPanelOpen: false,

  // Providers list (for admin dashboard scope dropdown)
  providersList: [],

  //map:
  timeRangeMap: {
    'last_7_days': 'Last 7 days',
    'last_30_days': 'Last 30 days',
    'last_90_days': 'Last 90 days',
    'last_year': 'Last year',
    'custom': 'Custom Range',
  },
});

const clinicDashboard = {
  namespaced: true,

  state: getDefaultState(),

  getters: {
    // Get Top KPIs metric IDs
    topKpis: (state) => state.kpis.pinned_metric_ids,

    // Get Key Metrics metric IDs
    keyMetrics: (state) => state.kpis.key_metric_ids,

    // Get a specific metric by ID
    getMetricById: (state) => (metricId) => state.metrics[metricId] || null,

    // Get patient growth chart series data
    patientGrowthChartData: (state) => state.metrics.patient_growth?.series || [],

    // Get patient engagement chart series data
    patientEngagementChartData: (state) => state.metrics.engaged_conversation_count?.series || [],

    // Get patient engagement quality details
    patientEngagementQualityDetails: (state) => state.patientEngagementQualitySummary?.engagement_quality,

    // Get order and revenue chart data
    orderRevenueChartBucketsData: (state) => state.orderRevenueChartData.buckets,

    // Get order and revenue totals
    orderRevenueTotals: (state) => ({
      total_created: state.orderRevenueChartData.total_created_orders_amount,
      total_canceled: state.orderRevenueChartData.total_canceled_orders_amount,
      total_net: state.orderRevenueChartData.total_net_order_amount,
    }),

    // Get practice documents table data
    practiceDocumentsTableData: (state) => state.practiceDocumentsData.items,

    // Get practice documents table total count
    practiceDocumentsTableTotal: (state) => state.practiceDocumentsData.total,

    // Get transactions table data
    transactionsTableData: (state) => state.transactionsData.transactions,

    // Get transactions table total count
    transactionsTableTotal: (state) => state.transactionsData.total,

    // Get AI insights
    aiInsights: (state) => state.aiInsights,

    // Get current clinic info based on view role
    currentClinicInfo: (state, _getters, _rootState, rootGetters) => {
      const isAdmin = rootGetters['login/isAdmin'];
      return isAdmin && state.dashboardFilters.dashboardScope === 'all'
        ? state.clinicInfoAdminLevel
        : state.clinicInfoProviderLevel;
    },
  },

  mutations: {
    SET_PAGE_LOADING(state, isLoading) {
      state.isPageLoading = isLoading;
    },

    SET_CLINIC_INFO_ADMIN_LEVEL(state, clinicInfo) {
      state.clinicInfoAdminLevel = clinicInfo;
    },

    SET_CLINIC_INFO_PROVIDER_LEVEL(state, clinicInfo) {
      state.clinicInfoProviderLevel = clinicInfo;
    },

    SET_DASHBOARD_FILTERS(state, filters) {
      state.dashboardFilters = { ...state.dashboardFilters, ...filters };
      // Persist filters to localStorage
      try {
        localStorage.setItem(DASHBOARD_FILTERS_STORAGE_KEY, JSON.stringify(state.dashboardFilters));
      } catch (error) {
        console.error('Failed to save dashboard filters to localStorage:', error);
      }
    },

    SET_METRICS(state, metricsArray) {
      const metricsObj = {};
      metricsArray.forEach((metric) => {
        metricsObj[metric.metric_id] = metric;
      });
      state.metrics = metricsObj;
    },
    SET_CACHE_UPDATE_TIME(state, time) {
      state.cacheUpdateTime = time;
    },

    SET_KPIS_PREFERENCES(state, kpis) {
      state.kpis = kpis;
    },

    SET_PATIENT_ENGAGEMENT_QUALITY(state, data) {
      state.patientEngagementQualitySummary = data;
    },

    SET_PATIENT_ENGAGEMENT_QUALITY_LOADING(state, isLoading) {
      state.patientEngagementQualityLoading = isLoading;
    },

    SET_ACTIVE_ALERTS(state, alerts) {
      state.activeAlertsData = alerts;
    },

    SET_ACTIVE_ALERTS_LOADING(state, isLoading) {
      state.activeAlertsLoading = isLoading;
    },

    SET_ORDER_REVENUE_CHART_DATA(state, data) {
      console.log('SET_ORDER_REVENUE_CHART_DATA', data);
      state.orderRevenueChartData = data;
    },

    SET_ORDER_REVENUE_CHART_LOADING(state, isLoading) {
      state.orderRevenueChartLoading = isLoading;
    },

    SET_PRACTICE_DOCUMENTS_DATA(state, data) {
      state.practiceDocumentsData = data;
    },

    SET_PRACTICE_DOCUMENTS_TABLE_FILTERS(state, filters) {
      state.practiceDocumentsTableFilters = { ...state.practiceDocumentsTableFilters, ...filters };
      // Persist filters to localStorage
      try {
        localStorage.setItem(DOCUMENTS_TABLE_FILTERS_STORAGE_KEY, JSON.stringify(state.practiceDocumentsTableFilters));
      } catch (error) {
        console.error('Failed to save documents table filters to localStorage:', error);
      }
    },

    SET_CHECKED_PRACTICE_DOCUMENT_IDS(state, ids) {
      state.checkedPracticeDocumentIds = ids;
    },

    SET_PRACTICE_DOCUMENTS_TABLE_LOADING(state, isLoading) {
      state.practiceDocumentsTableLoading = isLoading;
    },

    SET_TRANSACTIONS_DATA(state, data) {
      state.transactionsData = data;
    },

    SET_TRANSACTIONS_TABLE_FILTERS(state, filters) {
      state.transactionsTableFilters = { ...state.transactionsTableFilters, ...filters };
      // Persist filters to localStorage
      try {
        localStorage.setItem(TRANSACTIONS_TABLE_FILTERS_STORAGE_KEY, JSON.stringify(state.transactionsTableFilters));
      } catch (error) {
        console.error('Failed to save transactions table filters to localStorage:', error);
      }
    },

    SET_CHECKED_TRANSACTION_IDS(state, ids) {
      state.checkedTransactionIds = ids;
    },

    SET_TRANSACTIONS_TABLE_LOADING(state, isLoading) {
      state.transactionsTableLoading = isLoading;
    },

    SET_AI_INSIGHTS(state, insights) {
      state.aiInsights = insights;
    },

    SET_AI_INSIGHTS_LOADING(state, isLoading) {
      state.aiInsightsLoading = isLoading;
    },

    SET_AI_PANEL_OPEN(state, isOpen) {
      state.aiPanelOpen = isOpen;
    },

    SET_PROVIDERS_LIST(state, providers) {
      state.providersList = providers;
    },

    RESET_STATE(state) {
      Object.assign(state, getDefaultState());
    },
  },

  actions: {
    // ========================================================================
    // Helper: build common params from filters
    // ========================================================================
    _buildDateParams({ state }) {
      const { timeRange, startDate, endDate } = state.dashboardFilters;
      const params = { date_range_preset: timeRange };
      if (timeRange === 'custom') {
        params.date_range_start = startDate;
        params.date_range_end = endDate;
      }
      return params;
    },

    _buildProviderParam({ state, rootState, rootGetters }) {
      const { dashboardScope } = state.dashboardFilters;
      const isAdmin = rootGetters['login/isAdmin'];
      if (!isAdmin) {
        return { provider_id: rootState.login?.userInfo?.customer_id };
      } else if (isAdmin && dashboardScope !== 'all') {
        return { provider_id: dashboardScope };
      }
      return {};
    },

    // ========================================================================
    // Initialize dashboard
    // ========================================================================
    async initializeDashboard({ commit, dispatch, rootState }) {
      commit('SET_PAGE_LOADING', true);
      
      try {
        // Restore filters from localStorage if present
        try {
          const savedFilters = localStorage.getItem(DASHBOARD_FILTERS_STORAGE_KEY);
          if (savedFilters) {
            const parsedFilters = JSON.parse(savedFilters);
            // Validate and merge with current state
            commit('SET_DASHBOARD_FILTERS', parsedFilters);
          }
        } catch (error) {
          console.error('Failed to load dashboard filters from localStorage:', error);
          // Continue with default filters if localStorage fails
        }

        // Wait for critical data (metrics and preferences) — these control page loading
        await Promise.allSettled([
          dispatch('fetchClinicDetails'),
          dispatch('fetchProvidersList'), 
          dispatch('fetchMetrics'),
          dispatch('fetchKPIsPreferences'),
        ]);

        // Fire other fetches asynchronously — they have their own loading states
        dispatch('fetchPatientEngagementQualityData');
        dispatch('fetchOrdersAndRevenueChartData');
        dispatch('fetchPracticeDocumentsData');
        dispatch('fetchTransactionsData');

        // AI Insights are NOT fetched automatically
      } catch (error) {
        console.error('Error initializing dashboard:', error);
      } finally {
        commit('SET_PAGE_LOADING', false);
      }
    },

    // ========================================================================
    // Fetch clinic details (GraphQL)
    // ========================================================================
    async fetchClinicDetails({ commit, state, rootState }) {
      try {
        const userInfo = rootState.login.userInfo || {};
        
        // Get customer_id and clinic_id from userInfo
        const customer_id = userInfo.customer_id || null;
        const clinic_id = userInfo.clinic_id || null;

        // Fetch clinic details from GraphQL
        const clinicDetails = await fetchClinicDetailsApi({ customer_id: String(customer_id), clinic_id: String(clinic_id) });

        // Map and commit admin-level clinic info (practiceInfoPractice)
        if (clinicDetails.practiceInfoPractice) {
          const adminClinicInfo = clinicDetails.practiceInfoPractice;
          commit('SET_CLINIC_INFO_ADMIN_LEVEL', adminClinicInfo);
        }

        // Map and commit provider-level clinic info (practiceInfoPersonal)
        if (clinicDetails.practiceInfoPersonal) {
          const providerClinicInfo = clinicDetails.practiceInfoPersonal;
          commit('SET_CLINIC_INFO_PROVIDER_LEVEL', providerClinicInfo);
        }
      } catch (error) {
        console.error('Error fetching clinic details:', error);
      }
    },

    // ========================================================================
    // Update filters and refresh relevant data
    // ========================================================================
    async updateFilters({ commit, dispatch, state }, newFilters) {
      commit('SET_DASHBOARD_FILTERS', newFilters);

      await Promise.allSettled([
        dispatch('fetchClinicDetails'),
        dispatch('fetchMetrics'),
        dispatch('fetchKPIsPreferences'),
      ]);

      dispatch('fetchPatientEngagementQualityData');
      dispatch('fetchOrdersAndRevenueChartData');
      dispatch('fetchPracticeDocumentsData');
      dispatch('fetchTransactionsData');

      // Handle AI Insights based on panel state
      if (state.aiPanelOpen) {
        // If AI panel is open, refresh insights with new filter params
        dispatch('fetchAIInsightsData');
      } else {
        // If AI panel is closed, reset insights so they'll be fetched with updated params when panel opens
        commit('SET_AI_INSIGHTS', null);
      }
    },

    // ========================================================================
    // Fetch metrics
    // ========================================================================
    async fetchMetrics({ commit, dispatch, state, rootState }) {
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const dateParams = await dispatch('_buildDateParams');
        const providerParam = await dispatch('_buildProviderParam');
        const params = {
          clinic_id: clinicId,
          ...dateParams,
          ...providerParam,
        };

        const response = await getMetricsApi(params);
        commit('SET_METRICS', response.metrics);
        commit('SET_CACHE_UPDATE_TIME', response.cached_at);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      }
    },

    // ========================================================================
    // Fetch KPIs preferences
    // ========================================================================
    async fetchKPIsPreferences({ commit, dispatch, state, rootState }) {
      try {
        const providerParam = await dispatch('_buildProviderParam');
        const response = await getPreferencesApi(providerParam);
        
        // If preferences and key metrics are empty, set defaults
        if ((!response.pinned_metric_ids || response.pinned_metric_ids.length === 0) && 
            (!response.key_metric_ids || response.key_metric_ids.length === 0)) {
          const defaultPinned = ['active_patients', 'patient_growth', 'engagement_rate'];
          const allMetrics = (state.metrics && Object.keys(state.metrics).length > 0 
          ? Object.keys(state.metrics)
          : [
            'active_patients',
            'patient_growth',
            'engagement_rate',
            'engaged_conversation_count',
            'order_cancellation_rate',
            'median_provider_response_time',
            'revenue_growth',
          ]);
          const defaultKeyMetrics = allMetrics.filter(m => !defaultPinned.includes(m));
          
          const defaultPreferences = {
            pinned_metric_ids: defaultPinned,
            key_metric_ids: defaultKeyMetrics,
            dashboard_scope: response.dashboard_scope || 'practice_view',
            updated_at: null,
          };
          
          commit('SET_KPIS_PREFERENCES', defaultPreferences);
        } else {
          commit('SET_KPIS_PREFERENCES', response);
        }
      } catch (error) {
        console.error('Error fetching KPIs preferences:', error);
      }
    },

    // ========================================================================
    // Fetch patient engagement quality data
    // ========================================================================
    async fetchPatientEngagementQualityData({ commit, dispatch, state, rootState }) {
      commit('SET_PATIENT_ENGAGEMENT_QUALITY_LOADING', true);
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const dateParams = await dispatch('_buildDateParams');

        const requestBody = {
          clinic_id: clinicId,
          ...dateParams,
        };

        const response = await retrieveConversationsApi(requestBody);
        commit('SET_PATIENT_ENGAGEMENT_QUALITY', response);
      } catch (error) {
        console.error('Error fetching patient engagement quality:', error);
      } finally {
        commit('SET_PATIENT_ENGAGEMENT_QUALITY_LOADING', false);
      }
    },

    // ========================================================================
    // Fetch active alerts data
    // ========================================================================
    async fetchActiveAlertsData({ commit, dispatch, state, rootState }) {
      commit('SET_ACTIVE_ALERTS_LOADING', true);
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const providerParam = await dispatch('_buildProviderParam');

        let allAlerts = [];
        let cursor = null;

        // Loop through all pages until next_cursor is null
        do {
          const params = {
            clinic_id: clinicId,
            status: 'active',
            ...providerParam,
            ...(cursor && { cursor }),
          };

          const response = await getRiskAlertsApi(params);
          
          // Accumulate alerts from this page
          if (response.data && Array.isArray(response.data)) {
            allAlerts = allAlerts.concat(response.data);
          }

          // Update cursor for next iteration
          cursor = response.next_cursor || null;
        } while (cursor !== null);

        // Collect unique patient IDs from all alerts
        const patientIds = [...new Set(allAlerts.map(alert => alert.patient_id).filter(Boolean))];

        // Fetch patient names if there are patient IDs
        if (patientIds.length > 0) {
          const patients = await fetchPatientsByIdsApi(patientIds);
          
          // Create a map of patient_id to patient info for quick lookup
          const patientMap = {};
          patients.forEach(patient => {
            if (patient.patient_id) {
              patientMap[patient.patient_id] = {
                first_name: patient.patient_first_name || '',
                last_name: patient.patient_last_name || '',
                middle_name: patient.patient_middle_name || '',
              };
            }
          });

          // Enrich alerts with patient names
          allAlerts = allAlerts.map(alert => ({
            ...alert,
            patient_info: patientMap[alert.patient_id] || null,
          }));
        }

        commit('SET_ACTIVE_ALERTS', allAlerts);
      } catch (error) {
        console.error('Error fetching active alerts:', error);
      } finally {
        commit('SET_ACTIVE_ALERTS_LOADING', false);
      }
    },

    // ========================================================================
    // Fetch orders and revenue chart data
    // ========================================================================
    async fetchOrdersAndRevenueChartData({ commit, dispatch, state, rootState }) {
      commit('SET_ORDER_REVENUE_CHART_LOADING', true);
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const dateParams = await dispatch('_buildDateParams');
        const providerParam = await dispatch('_buildProviderParam');

        const params = {
          clinic_id: clinicId,
          ...dateParams,
          ...providerParam,
        };

        const response = await getOrderRevenueMovementApi(params);
        commit('SET_ORDER_REVENUE_CHART_DATA', response);
      } catch (error) {
        console.error('Error fetching order revenue chart data:', error);
      } finally {
        commit('SET_ORDER_REVENUE_CHART_LOADING', false);
      }
    },

    // ========================================================================
    // Fetch practice documents data
    // ========================================================================
    async fetchPracticeDocumentsData({ commit, state, rootState }) {
      commit('SET_PRACTICE_DOCUMENTS_TABLE_LOADING', true);
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const { search, documentType, page, pageSize } = state.practiceDocumentsTableFilters;

        const params = {
          clinic_id: clinicId,
          limit: pageSize,
          offset: (page - 1) * pageSize,
          source: "chart",
        };
        if (search) params.search = search;
        if (documentType) params.mime_type = documentType;

        const response = await getDocumentsApi(params);
        commit('SET_PRACTICE_DOCUMENTS_DATA', response);
      } catch (error) {
        console.error('Error fetching practice documents:', error);
      } finally {
        commit('SET_PRACTICE_DOCUMENTS_TABLE_LOADING', false);
      }
    },

    // ========================================================================
    // Delete practice documents in batch
    // ========================================================================
    async deletePracticeDocumentsBatch({ rootState, dispatch }, documentIds) {
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        if (!clinicId || !documentIds || documentIds.length === 0) {
          throw new Error('Missing clinic ID or document IDs');
        }

        const response = await batchDeleteDocumentsApi({
          clinic_id: clinicId,
          ids: documentIds
        });

        // Refresh the documents list after deletion
        await dispatch('fetchPracticeDocumentsData');
        
        return response;
      } catch (error) {
        console.error('Error batch deleting documents:', error);
        throw error;
      }
    },

    // ========================================================================
    // Fetch transactions data
    // ========================================================================
    async fetchTransactionsData({ commit, dispatch, state, rootState }) {
      commit('SET_TRANSACTIONS_TABLE_LOADING', true);
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const providerParam = await dispatch('_buildProviderParam');
        const { search, payerType, transactionType, paymentMethod, page, pageSize, timeRange, startDate, endDate } = state.transactionsTableFilters;

        const params = {
          clinic_id: clinicId,
          page_size: pageSize,
          page: page,
          date_range_preset: timeRange,
          ...(startDate ? { date_range_start: startDate } : {}),
          ...(endDate ? { date_range_end: endDate } : {}),
          ...providerParam,
        };
        if (search) params.search_query = search;
        if (payerType) params.payer_type = payerType;
        if (transactionType) params.transaction_type = transactionType;
        if (paymentMethod) params.payment_method = paymentMethod;

        const response = await getTransactionsApi(params);
        commit('SET_TRANSACTIONS_DATA', response);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        commit('SET_TRANSACTIONS_TABLE_LOADING', false);
      }
    },

    // ========================================================================
    // Fetch AI insights — only called when user clicks AI insight button
    // ========================================================================
    async fetchAIInsightsData({ commit, dispatch, state, rootState }) {
      commit('SET_AI_INSIGHTS_LOADING', true);
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const dateParams = await dispatch('_buildDateParams');
        const providerParam = await dispatch('_buildProviderParam');

        const params = {
          clinic_id: clinicId,
          insight_type: 'executive_summary',
          ...dateParams,
          ...providerParam,
        };

        const response = await getAIInsightsApi(params);
        commit('SET_AI_INSIGHTS', response);
      } catch (error) {
        console.error('Error fetching AI insights:', error);
      } finally {
        commit('SET_AI_INSIGHTS_LOADING', false);
      }
    },

    // ========================================================================
    // Update KPIs preferences
    // ========================================================================
    async updateKPIsPreferences({ commit, dispatch }, { pinned_metric_ids, key_metric_ids, dashboard_scope }) {
      try {
        const providerParam = await dispatch('_buildProviderParam');
        const requestBody = { pinned_metric_ids, key_metric_ids, dashboard_scope };
        const response = await updatePreferencesApi(requestBody, providerParam);
        commit('SET_KPIS_PREFERENCES', response);
        return response;
      } catch (error) {
        console.error('Error updating KPIs preferences:', error);
        throw error;
      }
    },

    // ========================================================================
    // Resolve active alert
    // ========================================================================
    async resolveActiveAlert({ dispatch, rootState }, alertId) {
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const providerId = rootState.login?.userInfo?.customer_id;
        await resolveRiskAlertApi(alertId, { clinic_id: clinicId, provider_id: providerId });
        await dispatch('fetchActiveAlertsData');
      } catch (error) {
        console.error('Error resolving alert:', error);
        throw error;
      }
    },

    // ========================================================================
    // Export transactions as CSV
    // ========================================================================
    async exportTransactions({ dispatch, state, rootState }, { selectedIds = null } = {}) {
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const providerParam = await dispatch('_buildProviderParam');
        const { search, payerType, transactionType, paymentMethod } = state.transactionsTableFilters;

        const requestBody = {
          clinic_id: clinicId,
          ...providerParam,
        };
        
        // If selectedIds provided, export only those transactions
        if (selectedIds && selectedIds.length > 0) {
          requestBody.record_ids = selectedIds;
        } else {
          // Otherwise export all matching filters
          if (search) requestBody.search_query = search;
          if (payerType) requestBody.payer = payerType;
          if (transactionType) requestBody.transaction_type = transactionType;
          if (paymentMethod) requestBody.payment_method = paymentMethod;
        }

        const response = await exportTransactionsApi(requestBody);

        if (response.sync) {
          const blob = new Blob([response.csv_content], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = response.file_name;
          link.click();
          window.URL.revokeObjectURL(url);
        } else {
          return response.export_id;
        }
      } catch (error) {
        console.error('Error exporting transactions:', error);
        throw error;
      }
    },

    // ========================================================================
    // Download async export
    // ========================================================================
    async downloadAsyncExport(_, exportId) {
      try {
        const data = await downloadTransactionExportApi(exportId);
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'clinic-charting-transactions.csv';
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading export:', error);
        throw error;
      }
    },

    // ========================================================================
    // Submit AI feedback
    // ========================================================================
    async submitAIFeedback({ rootState }, { insight_type, generated_at, feedback, notes }) {
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        const providerId = rootState.login?.userInfo?.customer_id;
        await submitAIFeedbackApi({
          clinic_id: clinicId,
          provider_id: providerId,
          insight_type,
          generated_at,
          feedback,
          notes,
        });
      } catch (error) {
        console.error('Error submitting AI feedback:', error);
        throw error;
      }
    },

    // ========================================================================
    // Fetch providers list (admin only)
    // ========================================================================
    async fetchProvidersList({ commit, rootState }) {
      try {
        const clinicId = rootState.login?.userInfo?.clinic_id;
        if (!clinicId) return;
        const response = await getCustomersByClinicIdApi(clinicId);
        const customers = response.data?.customers || [];
        const activeProviders = customers.filter(
          (c) => c.isActive && c.customer_id != null && c.customer_id !== ''
        );
        commit('SET_PROVIDERS_LIST', activeProviders);
      } catch (error) {
        console.error('Error fetching providers list:', error);
      }
    },

    // ========================================================================
    // Toggle AI Panel
    // ========================================================================
    toggleAIPanel({ commit, state, dispatch }) {
      const newState = !state.aiPanelOpen;
      commit('SET_AI_PANEL_OPEN', newState);
      if (newState && !state.aiInsights) {
        dispatch('fetchAIInsightsData');
      }
    },

    // ========================================================================
    // Toggle metric pin (add/remove from pinned metrics)
    // ========================================================================
    async toggleMetricPin({ state, dispatch }, metricId) {
      try {
        const currentPinned = [...state.kpis.pinned_metric_ids];
        const currentKeyMetrics = [...state.kpis.key_metric_ids];
        
        let newPinned;
        let newKeyMetrics;
        
        if (currentPinned.includes(metricId)) {
          // Unpin: remove from pinned, add to key metrics
          newPinned = currentPinned.filter(id => id !== metricId);
          newKeyMetrics = [...currentKeyMetrics, metricId];
        } else {
          // Pin: add to pinned, remove from key metrics
          newPinned = [...currentPinned, metricId];
          newKeyMetrics = currentKeyMetrics.filter(id => id !== metricId);
        }
        
        await dispatch('updateKPIsPreferences', {
          pinned_metric_ids: newPinned,
          key_metric_ids: newKeyMetrics,
          dashboard_scope: state.kpis.dashboard_scope,
        });
      } catch (error) {
        console.error('Error toggling metric pin:', error);
        throw error;
      }
    },
  },
};

export default clinicDashboard;
